import * as THREE from "three";

export class Player {
  constructor(scene, blockManager) {
    this.scene = scene;
    this.blockManager = blockManager;

    // Player state
    this.isJumping = false;
    this.isFalling = false;
    this.velocity = 0;
    this.isColliding = false;

    // Position player at starting position
    this.position = { x: 0, y: -3.75, z: 0 };
    this.defaultSpeed = 0.1;
    this.speed = this.defaultSpeed;
    this.initialSpeed = 0.08;

    // Physics constants - base values at 60fps
    this.baseGravity = 0.015; // Reduced from 0.015 for slower falling
    this.baseJumpForce = 0.28; // Reduced from 0.25 for balanced jump height
    this.terminalVelocity = -0.3; // Maximum falling speed (negative because it's downward)

    // Speed multipliers for new control scheme
    this.fastSpeedMultiplier = 1.6;
    this.slowSpeedMultiplier = 0.4;
    this.isRightKeyHeld = false;
    this.isLeftKeyHeld = false;

    // Jump-related properties
    this.isJumpKeyHeld = false;
    this.jumpKeyPressed = false; // Flag to track if jump button was newly pressed
    this.jumpBufferFrames = 0;
    this.maxJumpBufferFrames = 4; // Allow jumping up to 4 frames after leaving ground
    this.jumpLandBuffer = 0;
    this.maxJumpLandBuffer = 3; // Allow jump input a few frames before landing

    // Wall slide / wall jump state
    this.isWallSliding = false; // Touching a wall while falling (cosmetic + enables wall jump)
    this.isWallJumping = false; // Locked to fixed horizontal velocity until landing/next wall
    this.wallDir = 0; // +1 = wall on the right (forward), -1 = wall on the left
    // Wall jumps spring off at a fixed run speed (the regular movement speed) with no
    // air control.
    this.wallJumpHorizontalSpeed = this.defaultSpeed; // 0.1
    this.wallJumpForce = this.baseJumpForce; // Vertical impulse (same as a normal jump)
    this.nearGroundThreshold = 0.5; // Don't start a slide if ground is this close below
    // While sliding, fall speed eases toward this reduced terminal instead of
    // hard-clamping, giving a soft "grip" feel. (Normal terminal is -0.3.)
    this.wallSlideTerminalVelocity = -0.1;
    this.wallSlideGrip = 0.15; // easing rate per 60fps frame toward the slide terminal

    // Dimensions (in Three.js units)
    // These dimensions work with our frustumHeight of 10
    // A frustumHeight of 10 with our target of 1000px height means
    // each unit is 100px, so 0.4 units = 40px, 0.5 units = 50px
    this.width = 0.4; // 40px at 1000px effective height
    this.height = 0.5; // 50px at 1000px effective height

    // Animation properties
    this.textureLoader = new THREE.TextureLoader();
    this.sprites = [];
    this.currentFrame = 0;
    this.baseAnimationSpeed = 40; // Frames per second at normal speed
    this.animationTimer = 0;

    // Load sprite textures
    this.loadSprites();

    // Create player mesh
    this.createMesh();

    // Add eyes to the player
    this.createEyes();
  }

  loadSprites() {
    // Load all sprite textures
    // Get the base path from the deployed location (if any)
    const basePath = import.meta.env.BASE_URL || "/";

    for (let i = 1; i <= 6; i++) {
      const paddedIndex = i.toString().padStart(2, "0");
      // Use the base URL provided by Vite to ensure correct asset loading in any environment
      const texturePath = `${basePath}sprites/player/player${paddedIndex}.png`;
      const texture = this.textureLoader.load(texturePath);
      texture.magFilter = THREE.NearestFilter; // For pixel art
      texture.minFilter = THREE.NearestFilter; // For pixel art
      this.sprites.push(texture);
    }

    // Distinct wall-cling pose (legs tucked) used while wall sliding. Single static
    // frame, mirrors the original game's spr_PlayerWall.
    const wallPath = `${basePath}sprites/player/playerWall.png`;
    this.wallSprite = this.textureLoader.load(wallPath);
    this.wallSprite.magFilter = THREE.NearestFilter;
    this.wallSprite.minFilter = THREE.NearestFilter;
  }

  createMesh() {
    // Use a plane geometry for the sprite
    const geometry = new THREE.PlaneGeometry(this.width, this.height);

    // Use MeshStandardMaterial with alphaTest instead of MeshBasicMaterial
    // This allows proper shadow casting based on the transparency of the texture
    const material = new THREE.MeshStandardMaterial({
      map: this.sprites[0],
      transparent: true,
      alphaTest: 0.1, // Lower threshold to capture more of the sprite outline for shadows
      side: THREE.DoubleSide,
      // For solid appearance regardless of lighting conditions
      roughness: 1.0,
      metalness: 0.0,
      // Make sprite pixels appear as intended despite shadows
      emissiveMap: this.sprites[0], // Use the same texture for emission
      emissiveIntensity: 1.0, // Full intensity to maintain original look
    });

    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.position.set(this.position.x, this.position.y, this.position.z);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = false; // Don't receive shadows

    // No longer need the separate shadow mesh - the sprite itself will cast shadows

    this.scene.add(this.mesh);
  }

  createEyes() {
    // Create parent object for eyes to make positioning relative to player easier
    this.eyesGroup = new THREE.Group();

    // Convert pixel measurements to Three.js units
    // Since 100px = 1 unit, 1px = 0.01 units
    const pxToUnit = 0.01;

    // Eye dimensions and positions in Three.js units
    const eyeTopMargin = 10 * pxToUnit;
    const eyeRightMargin = 3 * pxToUnit;
    const eyeGap = 3 * pxToUnit;
    const rightEyeWidth = 3 * pxToUnit;
    const leftEyeWidth = 9 * pxToUnit;
    const eyeHeight = 17 * pxToUnit;

    // Calculate positions (right eye is closer to right edge)
    // Position is from center of the geometry
    const rightEyeX = this.width / 2 - eyeRightMargin - rightEyeWidth / 2;
    const leftEyeX = rightEyeX - rightEyeWidth / 2 - eyeGap - leftEyeWidth / 2;
    const eyeY = this.height / 2 - eyeTopMargin - eyeHeight / 2;

    // Z position slightly in front of player sprite
    const eyeZ = 0.01;

    // Create right eye (smaller)
    const rightEyeGeometry = new THREE.PlaneGeometry(rightEyeWidth, eyeHeight);
    const rightEyeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.rightEye = new THREE.Mesh(rightEyeGeometry, rightEyeMaterial);
    this.rightEye.position.set(rightEyeX, eyeY, eyeZ);

    // Create left eye (wider)
    const leftEyeGeometry = new THREE.PlaneGeometry(leftEyeWidth, eyeHeight);
    const leftEyeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.leftEye = new THREE.Mesh(leftEyeGeometry, leftEyeMaterial);
    this.leftEye.position.set(leftEyeX, eyeY, eyeZ);

    // Initialize scale to 1,1,1 to ensure proper scaling later
    this.leftEye.scale.set(1, 1, 1);
    this.rightEye.scale.set(1, 1, 1);

    // Add eyes to group
    this.eyesGroup.add(this.rightEye);
    this.eyesGroup.add(this.leftEye);

    // Add group to player mesh
    this.mesh.add(this.eyesGroup);

    // Initially hide the eyes until game starts
    this.eyesGroup.visible = false;

    // Store original values for blinking animation
    this.eyeOriginalProperties = {
      height: eyeHeight,
      leftEyeY: this.leftEye.position.y,
      rightEyeY: this.rightEye.position.y,
      minBlinkHeight: 3 * pxToUnit, // 3px minimum height when blinking
    };

    // Blinking animation properties
    this.blinkProperties = {
      isBlinking: false,
      blinkTimer: 0,
      blinkInterval: Math.random() * 3 + 2, // Random interval between 2-5 seconds
      blinkDuration: 0.25, // Duration of blink in seconds
      blinkProgress: 0, // 0 to 1, represents progress of current blink
    };
  }

  // Method to show eyes when game starts
  showEyes() {
    if (this.eyesGroup) {
      this.eyesGroup.visible = true;
    }
  }

  updateAnimation(gameSpeed) {
    // Update eye blinking
    this.updateEyeBlinking(gameSpeed);

    // Wall sliding: show the static wall-cling pose (takes priority over collision/jump).
    if (this.isWallSliding) {
      if (this.mesh.material.map !== this.wallSprite) {
        this.mesh.material.map = this.wallSprite;
        this.mesh.material.emissiveMap = this.wallSprite;
        this.mesh.material.needsUpdate = true;
      }
      return;
    }

    // Don't animate if not moving forward (collision) or if jumping/falling
    if (this.isColliding) {
      // Use frame 0 when stopped
      if (this.mesh.material.map !== this.sprites[0]) {
        this.mesh.material.map = this.sprites[0];
        this.mesh.material.emissiveMap = this.sprites[0];
      }
      return;
    }

    if (!this.isJumping && !this.isFalling) {
      // Calculate the frame rate based on player speed
      const speedFactor = this.speed / this.defaultSpeed;
      const targetFPS = this.baseAnimationSpeed * speedFactor;
      const secondsPerFrame = 1 / targetFPS;

      // Increment timer in seconds
      this.animationTimer += (1 / 60) * gameSpeed; // Assuming roughly 60fps

      // Switch frames when we've accumulated enough time
      if (this.animationTimer >= secondsPerFrame) {
        this.currentFrame = (this.currentFrame + 1) % this.sprites.length;
        this.mesh.material.map = this.sprites[this.currentFrame];
        this.mesh.material.emissiveMap = this.sprites[this.currentFrame]; // Update emissive map too
        this.animationTimer = 0;
      }
    } else {
      // While jumping or falling, use a specific frame
      const jumpFrame = 1; // Using the second frame (index 1) for jump pose
      if (this.mesh.material.map !== this.sprites[jumpFrame]) {
        this.mesh.material.map = this.sprites[jumpFrame];
        this.mesh.material.emissiveMap = this.sprites[jumpFrame]; // Update emissive map too
      }
    }

    // Make sure texture updates are applied
    this.mesh.material.needsUpdate = true;
  }

  updateEyeBlinking(gameSpeed) {
    // Skip if eyes don't exist yet
    if (!this.eyesGroup || !this.leftEye || !this.rightEye) return;

    const deltaTime = (1 / 60) * gameSpeed; // Approximate time in seconds per frame

    if (this.blinkProperties.isBlinking) {
      // Currently in a blink animation
      this.blinkProperties.blinkProgress +=
        deltaTime / this.blinkProperties.blinkDuration;

      if (this.blinkProperties.blinkProgress >= 1) {
        // Blink complete, reset
        this.blinkProperties.isBlinking = false;
        this.blinkProperties.blinkProgress = 0;
        this.blinkProperties.blinkInterval = Math.random() * 3 + 2; // New random interval
        this.blinkProperties.blinkTimer = 0;

        // Reset eyes to original state
        // Reset scale directly instead of calling updateEyeGeometry
        this.leftEye.scale.y = 1;
        this.rightEye.scale.y = 1;
        this.leftEye.position.y = this.eyeOriginalProperties.leftEyeY;
        this.rightEye.position.y = this.eyeOriginalProperties.rightEyeY;
      } else {
        // Calculate blink animation
        // Use sine curve for more natural eyelid movement (slower at extremes)
        const t = Math.sin(this.blinkProperties.blinkProgress * Math.PI);

        // Calculate current height (linear interpolation from original height to min height and back)
        const currentHeight =
          this.eyeOriginalProperties.height -
          t *
            (this.eyeOriginalProperties.height -
              this.eyeOriginalProperties.minBlinkHeight);

        // Calculate the scale factor instead of creating new geometries
        const scaleFactor = currentHeight / this.eyeOriginalProperties.height;

        // Calculate Y position adjustment (eyes should move up from bottom and down from top)
        // Move up by 1/3 of the difference in height
        const yOffset =
          ((this.eyeOriginalProperties.height - currentHeight) * 1) / 3;

        // Update eye scales directly instead of calling updateEyeGeometry
        this.leftEye.scale.y = scaleFactor;
        this.rightEye.scale.y = scaleFactor;
        this.leftEye.position.y = this.eyeOriginalProperties.leftEyeY - yOffset;
        this.rightEye.position.y =
          this.eyeOriginalProperties.rightEyeY - yOffset;
      }
    } else {
      // Not blinking, update timer
      this.blinkProperties.blinkTimer += deltaTime;

      // Start a blink when interval is reached
      if (
        this.blinkProperties.blinkTimer >= this.blinkProperties.blinkInterval
      ) {
        this.blinkProperties.isBlinking = true;
        this.blinkProperties.blinkProgress = 0;
      }
    }
  }

  jump() {
    // Wall jump: if we're sliding on a wall, spring off it instead of a normal jump.
    // Horizontal velocity is locked away from the wall at a fixed run speed (handled in
    // update()); the player has no horizontal control until they land or grab another wall.
    if (this.isWallSliding) {
      // Require a fresh press — not a held key. `jumpKeyPressed` is only set on a true
      // key-down transition (setJumpKeyHeld guards on it), so holding jump through the
      // slide won't fire: the player must release and re-press, timing the wall jump to
      // launch at max height instead of sliding down.
      if (!this.jumpKeyPressed) return;

      this.isJumping = true;
      this.isFalling = false;
      this.isWallSliding = false;
      this.isWallJumping = true; // wallDir is retained; travel direction is -wallDir
      this.velocity = this.wallJumpForce;
      this.jumpBufferFrames = 0;
      this.jumpLandBuffer = 0;
      this.jumpKeyPressed = false;
      return;
    }

    // Only actually jump if:
    // 1. Player is on ground and has pressed jump
    // 2. OR player is within the buffer frames after falling off a platform
    if (
      (!this.isJumping &&
        !this.isFalling &&
        (this.jumpKeyPressed || this.jumpLandBuffer > 0)) ||
      (this.jumpBufferFrames > 0 && this.jumpKeyPressed)
    ) {
      // Simple immediate collision check above the player
      const ceilingCheckBounds = {
        left: this.position.x - this.width / 2,
        right: this.position.x + this.width / 2,
        top: this.position.y + this.height / 2 + 0.1, // Check just above the player
        bottom: this.position.y - this.height / 2,
      };

      // Check for immediate collisions
      const collision = this.blockManager.checkVerticalCollision(
        ceilingCheckBounds,
        this.baseJumpForce
      );

      // If there's no immediate ceiling, allow the jump
      // We'll handle any ceiling collisions during the normal update loop
      if (!collision.collision || collision.direction !== "up") {
        this.isJumping = true;
        this.isFalling = false; // Reset falling state
        this.jumpBufferFrames = 0; // Reset jump buffer
        this.jumpLandBuffer = 0; // Reset landing buffer
        this.velocity = this.baseJumpForce; // Initial velocity is based on the jump force constant
        this.jumpKeyPressed = false; // Reset the jump key pressed flag
      }
    } else if (this.isFalling && this.jumpKeyPressed) {
      // If player presses jump while falling, store it in the land buffer
      // This will be used once they land on the ground
      this.jumpLandBuffer = this.maxJumpLandBuffer;
      this.jumpKeyPressed = false; // Reset the flag since we've handled this press
    }
  }

  setJumpKeyHeld(isHeld) {
    // If key is pressed down (not already held), set the pressed flag
    if (isHeld && !this.isJumpKeyHeld) {
      this.jumpKeyPressed = true;
    }

    // Update the held state
    this.isJumpKeyHeld = isHeld;
  }

  // New methods for the updated control scheme
  setRightKeyHeld(isHeld) {
    this.isRightKeyHeld = isHeld;
    this.updateSpeed();
  }

  setLeftKeyHeld(isHeld) {
    this.isLeftKeyHeld = isHeld;
    this.updateSpeed();
  }

  updateSpeed() {
    if (this.isRightKeyHeld) {
      this.speed = this.defaultSpeed * this.fastSpeedMultiplier;
    } else if (this.isLeftKeyHeld) {
      this.speed = this.defaultSpeed * this.slowSpeedMultiplier;
    } else {
      this.speed = this.defaultSpeed;
    }
  }

  reset() {
    this.isJumping = false;
    this.isFalling = false;
    this.isColliding = false;
    this.velocity = 0;
    this.position = { x: 0, y: -3.75, z: 0 };
    this.speed = this.defaultSpeed;
    this.isRightKeyHeld = false;
    this.isLeftKeyHeld = false;
    this.currentFrame = 0;
    this.animationTimer = 0;
    this.isJumpKeyHeld = false;
    this.jumpKeyPressed = false;
    this.jumpBufferFrames = 0;
    this.jumpLandBuffer = 0;
    this.isWallSliding = false;
    this.isWallJumping = false;
    this.wallDir = 0;
    this.mesh.material.map = this.sprites[0];
    this.mesh.material.emissiveMap = this.sprites[0];
    this.mesh.scale.x = 1; // Clear any wall-slide/wall-jump facing flip
    this.mesh.position.set(this.position.x, this.position.y, this.position.z);
    this.mesh.visible = true; // Ensure player is visible on reset

    // Reset blinking animation
    if (this.blinkProperties) {
      this.blinkProperties.isBlinking = false;
      this.blinkProperties.blinkProgress = 0;
      this.blinkProperties.blinkTimer = 0;
      this.blinkProperties.blinkInterval = Math.random() * 3 + 2;

      // Reset eye geometry and position if they exist
      if (this.leftEye && this.rightEye && this.eyeOriginalProperties) {
        // Instead of recreating geometries, reset the scale
        this.leftEye.scale.y = 1;
        this.rightEye.scale.y = 1;
        this.leftEye.position.y = this.eyeOriginalProperties.leftEyeY;
        this.rightEye.position.y = this.eyeOriginalProperties.rightEyeY;
      }
    }
  }

  die() {
    // Hide the player mesh
    this.mesh.visible = false;

    // Return the exact position where the player died for blood particles
    // For consistency, use the actual position without any checks
    // (Game.js will handle the positioning for fall deaths)
    return {
      x: this.mesh.position.x,
      y: this.mesh.position.y,
      z: this.mesh.position.z,
    };
  }

  // Get player bounds for collision detection
  getBounds() {
    return {
      left: this.position.x - this.width / 2,
      right: this.position.x + this.width / 2,
      top: this.position.y + this.height / 2,
      bottom: this.position.y - this.height / 2,
    };
  }

  animateEyes(gameSpeed) {
    // Only animate the eyes, not the player movement or sprite animation
    this.updateEyeBlinking(gameSpeed);
  }

  update(gameSpeed) {
    // Get current player bounds for collision detection
    const playerBounds = this.getBounds();

    // ---- HORIZONTAL MOVEMENT ----
    // Horizontal velocity depends on state:
    //   - Wall sliding: 0. The player is stuck to the wall and must NOT drift off it.
    //     (Mirrors the original game's `if (wallslide) xspeed = 0`.) Without this, the
    //     normal forward speed would push the player right and immediately detach them
    //     from a left wall after a backward wall jump.
    //   - Wall jumping: a fixed run speed AWAY from the wall (-wallDir), which can be
    //     negative (backward) — the only way the player ever travels left.
    //   - Otherwise: normal forward speed.
    let hVel;
    if (this.isWallSliding) {
      hVel = 0;
    } else if (this.isWallJumping) {
      hVel = -this.wallDir * this.wallJumpHorizontalSpeed;
    } else {
      hVel = this.speed;
    }
    const nextX = this.position.x + hVel * gameSpeed;

    // Create predicted bounds for horizontal movement
    const predictedHorizontalBounds = {
      left: nextX - this.width / 2,
      right: nextX + this.width / 2,
      top: playerBounds.top,
      bottom: playerBounds.bottom,
    };

    // Side to test for wall contact: toward travel when moving, toward the wall we're
    // already stuck to when stationary (sliding). +1 right, -1 left.
    const moveDir = hVel > 0 ? 1 : hVel < 0 ? -1 : this.wallDir;

    // Track which side (if any) we're pressed against this frame: +1 right, -1 left.
    let touchingWallSide = 0;

    // Check for collision toward moveDir and resolve against the wall edge.
    if (moveDir >= 0) {
      const horizontalCollision = this.blockManager.checkHorizontalCollision(
        predictedHorizontalBounds
      );
      if (horizontalCollision.collision) {
        this.isColliding = true;
        this.position.x = horizontalCollision.blockX - this.width / 2;
        touchingWallSide = 1;
      } else {
        this.isColliding = false;
        this.position.x = nextX;
      }
    } else {
      const horizontalCollision = this.blockManager.checkHorizontalCollisionLeft(
        predictedHorizontalBounds
      );
      if (horizontalCollision.collision) {
        this.isColliding = true;
        this.position.x = horizontalCollision.blockRight + this.width / 2;
        touchingWallSide = -1;
      } else {
        this.isColliding = false;
        this.position.x = nextX;
      }
    }

    // ---- VERTICAL MOVEMENT ----
    if (this.isJumping || this.isFalling) {
      // Apply jump physics based on whether the jump key is held
      if (this.isJumping && this.velocity > 0) {
        if (!this.isJumpKeyHeld) {
          // If jump key released during upward movement, immediately cut momentum
          this.velocity = 0;
        }
        // Otherwise, let gravity naturally affect the jump arc
      }

      if (this.isWallSliding) {
        // Wall slide acts like a grip: instead of full gravity + a hard terminal clamp,
        // the fall speed eases toward a reduced wall-slide terminal velocity. This works
        // from either side — a fast fall decelerates up to it, a slow fall accelerates
        // down to it — so grabbing the wall reads as the player catching and gripping it.
        // (isWallSliding here is last frame's value; it persists across the slide.)
        const t = Math.min(1, this.wallSlideGrip * gameSpeed);
        this.velocity += (this.wallSlideTerminalVelocity - this.velocity) * t;
      } else {
        // Calculate gravity adjustment for current frame rate
        const gravityThisFrame = this.baseGravity * gameSpeed;

        // Apply gravity to velocity
        this.velocity = this.velocity - gravityThisFrame;

        // Apply terminal velocity limit
        if (this.velocity < this.terminalVelocity) {
          this.velocity = this.terminalVelocity;
        }
      }

      // Determine how many sub-steps to use based on velocity
      // Higher velocity = more sub-steps to prevent tunneling
      const absVelocity = Math.abs(this.velocity * gameSpeed);
      const subSteps = Math.max(1, Math.ceil(absVelocity / 0.2)); // 0.2 is roughly half the height of block
      const subStepVelocity = (this.velocity * gameSpeed) / subSteps;

      // Process sub-steps for high velocities
      let subStepCollision = false;
      let finalY = this.position.y;

      for (let step = 0; step < subSteps && !subStepCollision; step++) {
        // Calculate next position for this sub-step
        const subStepY = this.position.y + subStepVelocity;

        // Create predicted bounds for this sub-step
        const stepBounds = {
          left: this.position.x - this.width / 2,
          right: this.position.x + this.width / 2,
          top: subStepY + this.height / 2,
          bottom: subStepY - this.height / 2,
        };

        // Check for vertical collisions in this sub-step
        const verticalCollision = this.blockManager.checkVerticalCollision(
          stepBounds,
          this.velocity
        );

        if (verticalCollision.collision) {
          subStepCollision = true;

          if (verticalCollision.direction === "up") {
            // Hitting a ceiling - stop upward movement and position correctly
            finalY = verticalCollision.blockY - this.height / 2;
            this.velocity = 0; // Stop upward momentum immediately

            // Transition to falling when hitting ceiling
            this.isJumping = false;
            this.isFalling = true;

            // Apply a smaller immediate downward velocity
            this.velocity = -0.03; // Reduced from -0.05
          } else if (verticalCollision.direction === "down") {
            // Landing on a block - stop falling
            finalY = verticalCollision.blockY + this.height / 2;
            this.isJumping = false;
            this.isFalling = false;
            this.velocity = 0;
            // Landing ends any wall jump / wall slide and restores normal control.
            this.isWallJumping = false;
            this.isWallSliding = false;
          }
        } else {
          // No collision in this sub-step
          // Check for nearby ground for smoother landing
          const groundY = this.blockManager.findGroundYAtPosition(
            this.position.x,
            stepBounds.bottom
          );

          // If there is ground and we're falling onto it
          if (
            groundY !== null &&
            stepBounds.bottom <= groundY &&
            this.velocity < 0
          ) {
            // Set position exactly on top of the ground
            finalY = groundY + this.height / 2;
            this.isJumping = false;
            this.isFalling = false;
            this.velocity = 0;
            // Landing ends any wall jump / wall slide and restores normal control.
            this.isWallJumping = false;
            this.isWallSliding = false;
            subStepCollision = true;
          } else if (step === subSteps - 1) {
            // Last sub-step with no collision, apply the full movement
            finalY = subStepY;

            // If moving upward but now moving downward, transition to falling
            if (this.isJumping && this.velocity <= 0) {
              this.isJumping = false;
              this.isFalling = true;
            }
          } else {
            // Move to next sub-step, updating position
            this.position.y = subStepY;
          }
        }
      }

      // Apply the final calculated position
      this.position.y = finalY;
    } else {
      // Player is standing on the ground - no wall slide / wall jump possible.
      this.isWallSliding = false;
      this.isWallJumping = false;

      // Check if there's still ground underneath
      const updatedBounds = this.getBounds(); // Get updated bounds after horizontal movement

      // Use direct vertical collision check for more accurate ground detection
      const verticalCollision = this.blockManager.checkVerticalCollision(
        {
          left: updatedBounds.left,
          right: updatedBounds.right,
          top: updatedBounds.top,
          // Check slightly below player to detect when ground disappears
          bottom: updatedBounds.bottom - 0.05,
        },
        this.velocity
      );

      if (!verticalCollision.collision) {
        // No ground directly below - start falling
        this.isFalling = true;
        this.velocity = 0; // Start with zero velocity for a natural fall
        this.jumpBufferFrames = this.maxJumpBufferFrames; // Start jump buffer when leaving ground
      }
    }

    // Update jump buffer counter when falling
    if (this.isFalling) {
      if (this.jumpBufferFrames > 0) {
        this.jumpBufferFrames = Math.max(0, this.jumpBufferFrames - gameSpeed);
      }

      // Don't decrement the jump land buffer while falling
      // We want to preserve it until the player lands
    } else if (!this.isJumping) {
      // We're on the ground - check if we have a pending jump from landing
      if (this.jumpLandBuffer > 0) {
        this.jump(); // Trigger the jump without setting jumpKeyPressed again
        this.jumpLandBuffer = 0; // Clear the buffer after using it
      }
    }

    // ---- WALL SLIDE DETECTION ----
    // Engage a wall slide when airborne, descending, and pressed against a wall on the
    // side we're moving toward. This is cosmetic (sprite + facing) and gates the wall
    // jump — gravity is unchanged, the player keeps falling at the normal rate.
    const fallingNow =
      (this.isJumping || this.isFalling) && this.velocity <= 0;
    // Pressed against the wall on the side we're moving toward (or already stuck to).
    const towardWall = touchingWallSide !== 0 && touchingWallSide === moveDir;

    // Don't stick to short ledges: skip the slide if the ground is close below.
    let nearGround = false;
    if (touchingWallSide !== 0) {
      const playerBottom = this.position.y - this.height / 2;
      const groundY = this.blockManager.findGroundYAtPosition(
        this.position.x,
        playerBottom
      );
      nearGround =
        groundY !== null && playerBottom - groundY < this.nearGroundThreshold;
    }

    this.isWallSliding =
      fallingNow && touchingWallSide !== 0 && towardWall && !nearGround;
    if (this.isWallSliding) {
      this.wallDir = touchingWallSide;
      this.isWallJumping = false; // grabbing a wall ends the wall-jump lock
    }

    // Face out from the wall while sliding (eyes point away from the wall), and face the
    // direction of travel during a wall jump. The eyes are children of the mesh, so the
    // horizontal flip carries them along — matching the original game's image_xscale flip.
    this.mesh.scale.x =
      this.isWallSliding || this.isWallJumping ? -this.wallDir : 1;

    // Update mesh position
    this.mesh.position.set(this.position.x, this.position.y, this.position.z);

    // Update animation
    this.updateAnimation(gameSpeed);

    return true; // Player is still alive
  }

  getBoundingBox() {
    const boundingBox = new THREE.Box3().setFromObject(this.mesh);
    return boundingBox;
  }
}
